import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FileText, AlertTriangle, Star, Search, 
  Eye, Trash2, X, CheckCircle, 
  BarChart3, Users, AlertCircle, Shield,
  Ban, Plus, UserX, Edit2, Save, ChevronDown, ChevronUp
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import FormEditor from "./FormEditor";
import { useFeedbackData } from "../hooks/useFeedbackData";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const VIOLATION_CATEGORIES = ["None", "Professionalism Issue", "Conduct Violation", "Abuse of Power", "Policy Violation"];
const OUTCOMES = ["No Action", "Staff Warning", "Strike 1", "Strike 2", "Demotion Review", "Removal"];
const STATUSES = ["Open", "Under Review", "Closed", "Invalid"];
const RANKS = ["OWNERSHIP", "HIGH COMMAND", "HEAD ADMIN", "SENIOR ADMIN", "ADMIN", "SENIOR MOD", "MOD", "JRMOD"];

const SEVERITY_COLORS = {
  1: "text-blue-400 bg-blue-500/20 border-blue-500/50",
  2: "text-amber-400 bg-amber-500/20 border-amber-500/50",
  3: "text-orange-400 bg-orange-500/20 border-orange-500/50",
  4: "text-red-400 bg-red-500/20 border-red-500/50"
};

const STATUS_COLORS = {
  "Open": "text-amber-400 bg-amber-500/20",
  "Under Review": "text-blue-400 bg-blue-500/20",
  "Closed": "text-zinc-400 bg-gray-500/20",
  "Invalid": "text-rose-400 bg-rose-500/20"
};

const OVERLAY_ANIM = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };
const PANEL_ANIM = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 20 } };
const ROW_ANIM = { initial: { opacity: 0 }, animate: { opacity: 1 } };


const FeedbackDashboard = ({ currentUser, onClose, isAdmin, permissions = {}, discordId = "" }) => {
  const [activeTab, setActiveTab] = useState("reports");
  const [selectedReport, setSelectedReport] = useState(null);
  const [filters, setFilters] = useState({
    staff_username: "",
    severity: "",
    feedback_type: "",
    status: ""
  });

  // Blacklist form state
  const [blacklistForm, setBlacklistForm] = useState({ discord_id: "", discord_username: "", reason: "" });

  // Staff dropdown management state
  const [editingStaff, setEditingStaff] = useState(null);
  const [newStaffForm, setNewStaffForm] = useState({ name: "", username: "", rank: "MOD", role_title: "", user_id: "0" });
  const [showAddStaff, setShowAddStaff] = useState(false);

  const {
    reports, stats, flaggedStaff, blacklist, staffList, loading,
    refetchReports, refetchBlacklist, refetchStaffList,
  } = useFeedbackData(filters);

  const handleUpdateReport = async (reportId, updates) => {
    try {
      await axios.put(`${API}/feedback-reports/${reportId}?performed_by=${encodeURIComponent(currentUser)}`, updates);
      toast.success("Report updated");
      refetchReports();
      setSelectedReport(null);
    } catch (error) {
      toast.error("Failed to update report");
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report?")) return;
    try {
      await axios.delete(`${API}/feedback-reports/${reportId}?performed_by=${encodeURIComponent(currentUser)}`);
      toast.success("Report deleted");
      refetchReports();
    } catch (error) {
      toast.error("Failed to delete report");
    }
  };

  const handleAddToBlacklist = async () => {
    if (!blacklistForm.discord_id && !blacklistForm.discord_username) {
      toast.error("Discord ID or username required");
      return;
    }
    try {
      await axios.post(`${API}/feedback-blacklist`, {
        ...blacklistForm,
        added_by: currentUser
      });
      toast.success("User blacklisted");
      setBlacklistForm({ discord_id: "", discord_username: "", reason: "" });
      refetchBlacklist();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to blacklist user");
    }
  };

  const handleRemoveFromBlacklist = async (entryId) => {
    if (!window.confirm("Remove this user from the blacklist?")) return;
    try {
      await axios.delete(`${API}/feedback-blacklist/${entryId}?performed_by=${encodeURIComponent(currentUser)}`);
      toast.success("User removed from blacklist");
      refetchBlacklist();
    } catch (err) {
      toast.error("Failed to remove from blacklist");
    }
  };

  // Staff dropdown management
  const handleUpdateStaffRank = async (staffId, updates) => {
    try {
      await axios.put(`${API}/staff/${staffId}?performed_by=${encodeURIComponent(currentUser)}`, updates);
      toast.success("Staff member updated");
      setEditingStaff(null);
      refetchStaffList();
    } catch (err) {
      toast.error("Failed to update staff member");
    }
  };

  const handleAddStaff = async () => {
    if (!newStaffForm.name.trim() || !newStaffForm.username.trim()) {
      toast.error("Name and username are required");
      return;
    }
    try {
      await axios.post(`${API}/staff?performed_by=${encodeURIComponent(currentUser)}`, newStaffForm);
      toast.success("Staff member added");
      setNewStaffForm({ name: "", username: "", rank: "MOD", role_title: "", user_id: "0" });
      setShowAddStaff(false);
      refetchStaffList();
    } catch (err) {
      toast.error("Failed to add staff member");
    }
  };

  const handleDeleteStaff = async (staffId, staffName) => {
    if (!window.confirm(`Remove ${staffName} from the staff list?`)) return;
    try {
      await axios.delete(`${API}/staff/${staffId}?performed_by=${encodeURIComponent(currentUser)}`);
      toast.success(`${staffName} removed`);
      refetchStaffList();
    } catch (err) {
      toast.error("Failed to remove staff member");
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const tabs = [
    { id: "reports", label: "Reports", icon: FileText },
  ];
  if (permissions.blacklist) {
    tabs.push({ id: "blacklist", label: "Blacklist", icon: Ban });
  }
  if (permissions.staff_dropdown) {
    tabs.push({ id: "staff-manage", label: "Staff Dropdown", icon: Users });
  }
  if (permissions.form_editor) {
    tabs.push({ id: "form-editor", label: "Form Editor", icon: Edit2 });
  }

  return (
    <motion.div
      {...OVERLAY_ANIM}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        {...PANEL_ANIM}
        className="glass-card w-full max-w-6xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-none bg-blue-500/20 border border-blue-500/30">
              <FileText className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-white">Staff Feedback Dashboard</h2>
              <p className="text-zinc-400 text-sm">Staff Standards Command</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-none transition-colors" data-testid="close-dashboard-btn">
            <X className="w-6 h-6 text-zinc-400" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 pt-4 flex gap-2 border-b border-zinc-800">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-t-lg transition-colors text-sm font-medium ${
                activeTab === tab.id
                  ? "bg-zinc-800 text-white border-b-2 border-blue-500"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === "blacklist" && blacklist.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">{blacklist.length}</span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-6">
          {/* REPORTS TAB */}
          {activeTab === "reports" && (
            <>
              {/* Stats Cards */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="glass-card rounded-none p-4 border border-zinc-700">
                    <p className="text-zinc-500 text-xs uppercase">Total Reports</p>
                    <p className="text-2xl font-bold text-white">{stats.total_reports}</p>
                  </div>
                  <div className="glass-card rounded-none p-4 border border-amber-500/30 bg-amber-500/5">
                    <p className="text-amber-400 text-xs uppercase">Open</p>
                    <p className="text-2xl font-bold text-amber-400">{stats.open_reports}</p>
                  </div>
                  <div className="glass-card rounded-none p-4 border border-blue-500/30 bg-blue-500/5">
                    <p className="text-blue-400 text-xs uppercase">Under Review</p>
                    <p className="text-2xl font-bold text-blue-400">{stats.under_review}</p>
                  </div>
                  <div className="glass-card rounded-none p-4 border border-zinc-700">
                    <p className="text-zinc-500 text-xs uppercase">Closed</p>
                    <p className="text-2xl font-bold text-zinc-400">{stats.closed_reports}</p>
                  </div>
                  <div className="glass-card rounded-none p-4 border border-yellow-500/30 bg-yellow-500/5">
                    <p className="text-yellow-400 text-xs uppercase">Avg Rating</p>
                    <div className="flex items-center gap-1">
                      <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                      <p className="text-2xl font-bold text-yellow-400">{stats.average_rating}</p>
                    </div>
                  </div>
                  <div className="glass-card rounded-none p-4 border border-red-500/30 bg-red-500/5">
                    <p className="text-red-400 text-xs uppercase">Flagged Staff</p>
                    <p className="text-2xl font-bold text-red-400">{flaggedStaff.length}</p>
                  </div>
                </div>
              )}

              {/* Flagged Staff Alert */}
              {flaggedStaff.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-none p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <h3 className="font-bold text-red-400">Flagged for Review (3+ reports in 7 days)</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {flaggedStaff.map((staff) => (
                      <span key={staff.username} className="px-3 py-1 bg-red-500/20 rounded-full text-red-400 text-sm">
                        {staff.username} ({staff.report_count} reports)
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Member-Specific Ratings */}
              {stats?.staff_report_counts?.length > 0 && (
                <div className="glass-card rounded-none p-4 border border-zinc-700">
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="w-5 h-5 text-yellow-400" />
                    <h3 className="font-bold text-white text-sm">Member Ratings</h3>
                    <span className="text-zinc-500 text-xs">(excludes Invalid reports)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[200px] overflow-y-auto scrollbar-thin">
                    {stats.staff_report_counts.map((member) => {
                      const isPositiveOnly = member.positive_count > 0 && member.negative_count === 0 && member.poor_count === 0;
                      const displayRating = member.avg_negative_rating != null ? member.avg_negative_rating : member.avg_rating;
                      return (
                      <div key={member._id} className={`flex items-center justify-between p-2 rounded-none border ${
                        isPositiveOnly
                          ? "bg-emerald-500/5 border-emerald-500/30"
                          : "bg-zinc-800/50 border-zinc-700/50"
                      }`} data-testid={`member-rating-${member._id}`}>
                        <div className="flex-1 min-w-0">
                          <span className="text-white text-sm font-medium truncate block">{member._id}</span>
                          <div className="flex items-center gap-2 text-xs text-zinc-500">
                            <span>{member.count} reports</span>
                            {member.positive_count > 0 && <span className="text-emerald-400">+{member.positive_count} positive</span>}
                            {member.poor_count > 0 && <span className="text-orange-400">{member.poor_count} poor</span>}
                            {member.negative_count > 0 && <span className="text-red-400">{member.negative_count} conduct</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          {isPositiveOnly ? (
                            <span className="text-xs font-mono font-bold text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5">GOOD</span>
                          ) : (
                            <>
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                              <span className={`text-sm font-bold ${
                                displayRating >= 4 ? 'text-emerald-400' : displayRating >= 3 ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {displayRating ? displayRating.toFixed(1) : 'N/A'}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      placeholder="Search by staff username..."
                      value={filters.staff_username}
                      onChange={(e) => setFilters({ ...filters, staff_username: e.target.value })}
                      className="input-field w-full pl-10"
                      data-testid="search-reports-input"
                    />
                  </div>
                </div>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="select-field"
                >
                  <option value="">All Status</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                  value={filters.severity}
                  onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                  className="select-field"
                  data-testid="filter-severity"
                >
                  <option value="">All Severity</option>
                  <option value="1">1 - Minor</option>
                  <option value="2">2 - Moderate</option>
                  <option value="3">3 - Serious</option>
                  <option value="4">4 - Major</option>
                </select>
                <select
                  value={filters.feedback_type}
                  onChange={(e) => setFilters({ ...filters, feedback_type: e.target.value })}
                  className="select-field"
                  data-testid="filter-feedback-type"
                >
                  <option value="">All Types</option>
                  <option value="Positive Staff Feedback">Positive</option>
                  <option value="Poor Staff Feedback">Poor</option>
                  <option value="Staff Conduct">Staff Conduct</option>
                  <option value="Staff Professionalism">Professionalism</option>
                  <option value="Abuse of Power">Abuse of Power</option>
                  <option value="Inactivity">Inactivity</option>
                </select>
                <button
                  onClick={() => setFilters({ staff_username: "", severity: "", feedback_type: "", status: "" })}
                  className="btn-secondary"
                >
                  Reset
                </button>
              </div>

              {/* Reports List */}
              <div className="space-y-3 max-h-[500px] overflow-y-auto scrollbar-thin">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </div>
                ) : reports.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No reports found</p>
                  </div>
                ) : (
                  reports.map((report) => (
                    <motion.div
                      key={report.id}
                      {...ROW_ANIM}
                      className={`p-4 rounded-none border ${
                        report.feedback_type === "Positive Staff Feedback"
                          ? 'border-emerald-500/50 bg-emerald-500/5'
                          : flaggedStaff.some(f => f.username === report.staff_username)
                          ? 'border-red-500/50 bg-red-500/5'
                          : report.status === "Invalid"
                          ? 'border-rose-500/30 bg-rose-500/5 opacity-60'
                          : 'border-zinc-700 bg-zinc-800/30'
                      } hover:bg-zinc-800/50 transition-colors`}
                      data-testid={`report-card-${report.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className="font-bold text-white">{report.staff_username}</span>
                            {report.feedback_type === "Positive Staff Feedback" && (
                              <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" data-testid="positive-badge">
                                Positive
                              </span>
                            )}
                            {report.feedback_type === "Poor Staff Feedback" && (
                              <span className="px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-400 border border-orange-500/40" data-testid="poor-badge">
                                Poor
                              </span>
                            )}
                            {report.feedback_type !== "Positive Staff Feedback" && report.feedback_type !== "Poor Staff Feedback" && (
                              <span className={`px-2 py-0.5 rounded text-xs border ${SEVERITY_COLORS[report.severity]}`}>
                                Severity {report.severity}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[report.status]}`}>
                              {report.status}
                            </span>
                            {flaggedStaff.some(f => f.username === report.staff_username) && report.feedback_type !== "Positive Staff Feedback" && (
                              <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> Flagged
                              </span>
                            )}
                          </div>
                          <p className="text-zinc-400 text-sm">{report.feedback_type}</p>
                          <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{report.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-zinc-600">
                            <span>{formatDate(report.created_at)}</span>
                            <span className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                              {report.star_rating}/5
                            </span>
                            {report.is_anonymous ? (
                              <span className="text-zinc-500">Anonymous</span>
                            ) : (
                              <span>{report.reporter_roblox || report.reporter_discord || "Unknown"}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedReport(report)}
                            className="p-2 hover:bg-zinc-700 rounded-none transition-colors"
                            title="View/Edit"
                          >
                            <Eye className="w-4 h-4 text-zinc-400" />
                          </button>
                          <button
                            onClick={() => handleDeleteReport(report.id)}
                            className="p-2 hover:bg-red-500/20 rounded-none transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </>
          )}

          {/* BLACKLIST TAB */}
          {activeTab === "blacklist" && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <Ban className="w-5 h-5 text-red-400" />
                <h3 className="text-lg font-heading font-bold text-white">Feedback Blacklist</h3>
              </div>
              <p className="text-zinc-500 text-sm">
                Blacklisted users will be blocked from submitting feedback reports via Discord OAuth.
              </p>

              {/* Add to Blacklist Form */}
              <div className="glass-card rounded-none p-4 border border-zinc-700">
                <h4 className="text-sm font-bold text-white mb-3">Add User to Blacklist</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Discord ID</label>
                    <input
                      type="text"
                      value={blacklistForm.discord_id}
                      onChange={(e) => setBlacklistForm({ ...blacklistForm, discord_id: e.target.value })}
                      placeholder="e.g. 123456789012345678"
                      className="input-field w-full"
                      data-testid="blacklist-discord-id"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Discord Username</label>
                    <input
                      type="text"
                      value={blacklistForm.discord_username}
                      onChange={(e) => setBlacklistForm({ ...blacklistForm, discord_username: e.target.value })}
                      placeholder="e.g. user#1234"
                      className="input-field w-full"
                      data-testid="blacklist-discord-username"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Reason</label>
                    <input
                      type="text"
                      value={blacklistForm.reason}
                      onChange={(e) => setBlacklistForm({ ...blacklistForm, reason: e.target.value })}
                      placeholder="Reason for blacklisting"
                      className="input-field w-full"
                      data-testid="blacklist-reason"
                    />
                  </div>
                </div>
                <button
                  onClick={handleAddToBlacklist}
                  className="mt-3 px-4 py-2 bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 text-red-400 rounded-none transition-colors flex items-center gap-2 text-sm"
                  data-testid="add-blacklist-btn"
                >
                  <UserX className="w-4 h-4" />
                  Add to Blacklist
                </button>
              </div>

              {/* Blacklist Table */}
              <div className="space-y-2">
                {blacklist.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Ban className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No users blacklisted</p>
                  </div>
                ) : (
                  blacklist.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-3 rounded-none border border-zinc-700 bg-zinc-800/30">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <UserX className="w-4 h-4 text-red-400" />
                          <span className="text-white font-medium">{entry.discord_username || entry.discord_id}</span>
                          {entry.discord_id && entry.discord_username && (
                            <span className="text-zinc-500 text-xs">ID: {entry.discord_id}</span>
                          )}
                        </div>
                        <p className="text-zinc-500 text-xs mt-1">
                          Reason: {entry.reason || "No reason"} | By: {entry.added_by || "Unknown"} | {formatDate(entry.created_at)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveFromBlacklist(entry.id)}
                        className="p-2 hover:bg-emerald-500/20 rounded-none transition-colors"
                        title="Remove from blacklist"
                        data-testid={`remove-blacklist-${entry.id}`}
                      >
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* STAFF DROPDOWN MANAGEMENT TAB */}
          {activeTab === "staff-manage" && permissions.staff_dropdown && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-purple-400" />
                  <div>
                    <h3 className="text-lg font-heading font-bold text-white">Manage Staff Dropdown</h3>
                    <p className="text-zinc-500 text-sm">Add, remove, or update staff ranks shown in the feedback form.</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddStaff(!showAddStaff)}
                  className="px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 hover:bg-emerald-500/30 text-emerald-400 rounded-none transition-colors flex items-center gap-2 text-sm"
                  data-testid="toggle-add-staff-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Staff
                </button>
              </div>

              {/* Add Staff Form */}
              {showAddStaff && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="glass-card rounded-none p-4 border border-emerald-500/30 bg-emerald-500/5"
                >
                  <h4 className="text-sm font-bold text-white mb-3">Add New Staff Member</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Roblox Name *</label>
                      <input
                        type="text"
                        value={newStaffForm.name}
                        onChange={(e) => setNewStaffForm({ ...newStaffForm, name: e.target.value, username: e.target.value })}
                        placeholder="Roblox username"
                        className="input-field w-full"
                        data-testid="new-staff-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Rank *</label>
                      <select
                        value={newStaffForm.rank}
                        onChange={(e) => setNewStaffForm({ ...newStaffForm, rank: e.target.value })}
                        className="select-field w-full"
                        data-testid="new-staff-rank"
                      >
                        {RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Role Title</label>
                      <input
                        type="text"
                        value={newStaffForm.role_title}
                        onChange={(e) => setNewStaffForm({ ...newStaffForm, role_title: e.target.value })}
                        placeholder="Optional title"
                        className="input-field w-full"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={handleAddStaff}
                        className="w-full px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 hover:bg-emerald-500/30 text-emerald-400 rounded-none transition-colors flex items-center justify-center gap-2 text-sm"
                        data-testid="confirm-add-staff-btn"
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Staff List */}
              <div className="space-y-1 max-h-[500px] overflow-y-auto scrollbar-thin">
                {staffList.map((staff) => (
                  <div key={staff.id} className="flex items-center justify-between p-3 rounded-none border border-zinc-700 bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors">
                    {editingStaff === staff.id ? (
                      <StaffEditRow
                        staff={staff}
                        onSave={(updates) => handleUpdateStaffRank(staff.id, updates)}
                        onCancel={() => setEditingStaff(null)}
                      />
                    ) : (
                      <>
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-white font-medium w-48 truncate">{staff.name}</span>
                          <span className="px-2 py-0.5 rounded text-xs bg-zinc-700 text-zinc-300">{staff.rank}</span>
                          {staff.role_title && (
                            <span className="text-zinc-500 text-xs">{staff.role_title}</span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => setEditingStaff(staff.id)}
                            className="p-2 hover:bg-blue-500/20 rounded-none transition-colors"
                            title="Edit rank"
                            data-testid={`edit-staff-${staff.id}`}
                          >
                            <Edit2 className="w-4 h-4 text-blue-400" />
                          </button>
                          <button
                            onClick={() => handleDeleteStaff(staff.id, staff.name)}
                            className="p-2 hover:bg-red-500/20 rounded-none transition-colors"
                            title="Remove staff"
                            data-testid={`delete-staff-${staff.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FORM EDITOR TAB */}
          {activeTab === "form-editor" && permissions.form_editor && (
            <FormEditor discordId={discordId} />
          )}
        </div>

        {/* Report Detail Modal */}
        <AnimatePresence>
          {selectedReport && (
            <ReportDetailModal
              report={selectedReport}
              onClose={() => setSelectedReport(null)}
              onUpdate={handleUpdateReport}
              currentUser={currentUser}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

// Inline edit row for staff rank
const StaffEditRow = ({ staff, onSave, onCancel }) => {
  const [rank, setRank] = useState(staff.rank);
  const [roleTitle, setRoleTitle] = useState(staff.role_title || "");
  const [name, setName] = useState(staff.name);

  return (
    <div className="flex items-center gap-3 flex-1 flex-wrap">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="input-field w-40"
        placeholder="Name"
      />
      <select value={rank} onChange={(e) => setRank(e.target.value)} className="select-field">
        {RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <input
        type="text"
        value={roleTitle}
        onChange={(e) => setRoleTitle(e.target.value)}
        className="input-field w-32"
        placeholder="Role title"
      />
      <button
        onClick={() => onSave({ name, username: name, rank, role_title: roleTitle })}
        className="p-2 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-none transition-colors"
      >
        <Save className="w-4 h-4 text-emerald-400" />
      </button>
      <button
        onClick={onCancel}
        className="p-2 bg-zinc-700 hover:bg-slate-600 rounded-none transition-colors"
      >
        <X className="w-4 h-4 text-zinc-400" />
      </button>
    </div>
  );
};

const ReportDetailModal = ({ report, onClose, onUpdate, currentUser }) => {
  const [reviewData, setReviewData] = useState({
    violation_category: report.violation_category || "None",
    outcome: report.outcome || "No Action",
    review_notes: report.review_notes || "",
    status: report.status || "Open"
  });

  const handleSave = () => {
    onUpdate(report.id, { ...reviewData, handled_by: currentUser });
  };

  return (
    <motion.div
      {...OVERLAY_ANIM}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="glass-card w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between shrink-0">
          <h3 className="text-xl font-heading font-bold text-white">Report Details</h3>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-none">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-zinc-500 uppercase">Staff Member</p>
              <p className="text-white font-bold">{report.staff_username}</p>
              <p className="text-zinc-500 text-sm">{report.staff_rank}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">Reporter</p>
              <p className="text-white">
                {report.is_anonymous ? "Anonymous" : (report.reporter_roblox || report.reporter_discord || "Unknown")}
              </p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">Type</p>
              <p className="text-white">{report.feedback_type}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">Severity</p>
              <span className={`px-2 py-1 rounded text-sm border ${SEVERITY_COLORS[report.severity]}`}>
                Level {report.severity}
              </span>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">Rating</p>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${star <= report.star_rating ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">Location</p>
              <p className="text-white">{report.incident_location || "Not specified"}</p>
            </div>
          </div>

          <div>
            <p className="text-xs text-zinc-500 uppercase mb-2">Description</p>
            <p className="text-zinc-300 bg-zinc-800/50 rounded-none p-3">{report.description}</p>
          </div>

          {report.suggestions && (
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-2">Suggestions</p>
              <p className="text-zinc-400 bg-zinc-800/50 rounded-none p-3">{report.suggestions}</p>
            </div>
          )}

          {/* Assessment Fields (Positive/Poor Feedback) */}
          {(report.performance_feedback || report.activity_feedback || report.moderating_feedback || report.decision_making_feedback) && (
            <div className="border-t border-zinc-700 pt-4">
              <p className="text-xs text-zinc-500 uppercase mb-3">Staff Assessment</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {report.performance_feedback && (
                  <div className="bg-zinc-800/50 rounded-none p-3">
                    <p className="text-xs text-zinc-500 mb-1">Performance</p>
                    <p className="text-zinc-300 text-sm">{report.performance_feedback}</p>
                  </div>
                )}
                {report.activity_feedback && (
                  <div className="bg-zinc-800/50 rounded-none p-3">
                    <p className="text-xs text-zinc-500 mb-1">Activity</p>
                    <p className="text-zinc-300 text-sm">{report.activity_feedback}</p>
                  </div>
                )}
                {report.moderating_feedback && (
                  <div className="bg-zinc-800/50 rounded-none p-3">
                    <p className="text-xs text-zinc-500 mb-1">Moderating</p>
                    <p className="text-zinc-300 text-sm">{report.moderating_feedback}</p>
                  </div>
                )}
                {report.decision_making_feedback && (
                  <div className="bg-zinc-800/50 rounded-none p-3">
                    <p className="text-xs text-zinc-500 mb-1">Decision Making</p>
                    <p className="text-zinc-300 text-sm">{report.decision_making_feedback}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dynamic Custom Answers */}
          {report.custom_answers?.length > 0 && (
            <div className="border-t border-zinc-700 pt-4">
              <p className="text-xs text-zinc-500 uppercase mb-3">Additional Responses</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {report.custom_answers.map((qa, i) => (
                  <div key={qa.question || `qa-${i}`} className="bg-zinc-800/50 rounded-none p-3">
                    <p className="text-xs text-zinc-500 mb-1">{qa.question}</p>
                    <p className="text-zinc-300 text-sm">{qa.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.evidence_links?.length > 0 && (
            <div>
              <p className="text-xs text-zinc-500 uppercase mb-2">Evidence</p>
              <div className="space-y-1">
                {report.evidence_links.map((link, i) => (
                  <a key={`evidence-link-${i}`} href={link} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline text-sm block">
                    {link}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Staff Standards Review */}
          <div className="border-t border-zinc-700 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-purple-400" />
              <h4 className="font-heading font-bold text-white">Staff Standards Review</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-zinc-500 uppercase mb-2">Violation Category</label>
                <select
                  value={reviewData.violation_category}
                  onChange={(e) => setReviewData({ ...reviewData, violation_category: e.target.value })}
                  className="select-field w-full"
                >
                  {VIOLATION_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 uppercase mb-2">Outcome</label>
                <select
                  value={reviewData.outcome}
                  onChange={(e) => setReviewData({ ...reviewData, outcome: e.target.value })}
                  className="select-field w-full"
                >
                  {OUTCOMES.map((out) => (
                    <option key={out} value={out}>{out}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-zinc-500 uppercase mb-2">Status</label>
                <div className="flex gap-2">
                  {STATUSES.map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setReviewData({ ...reviewData, status })}
                      className={`px-4 py-2 rounded-none transition-colors ${
                        reviewData.status === status
                          ? status === "Closed" ? "bg-gray-500/20 text-zinc-300 border border-gray-500/50"
                            : status === "Under Review" ? "bg-blue-500/20 text-blue-400 border border-blue-500/50"
                            : status === "Invalid" ? "bg-rose-500/20 text-rose-400 border border-rose-500/50"
                            : "bg-amber-500/20 text-amber-400 border border-amber-500/50"
                          : "bg-zinc-800 text-zinc-400 border border-zinc-700"
                      }`}
                      data-testid={`status-btn-${status.toLowerCase().replace(' ', '-')}`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-zinc-500 uppercase mb-2">Review Notes</label>
                <textarea
                  value={reviewData.review_notes}
                  onChange={(e) => setReviewData({ ...reviewData, review_notes: e.target.value })}
                  placeholder="Internal notes for Staff Standards..."
                  className="input-field w-full h-24 resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-zinc-800 flex gap-3 justify-end shrink-0">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} className="btn-primary">Save Changes</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FeedbackDashboard;
